// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

/** *
 * [Azure OpenAI](https://learn.microsoft.com/azure/cognitive-services/openai/overview)
 * provides REST API access to OpenAI's powerful language models including the GPT-3,
 * Codex and Embeddings model series. In addition, the new GPT-4 and ChatGPT (gpt-35-turbo)
 * model series have now reached general availability. These models can be easily adapted
 * to your specific task including but not limited to content generation, summarization,
 * semantic search, and natural language to code translation.
 *
 * @packageDocumentation
 */

export { AzureKeyCredential } from "@azure/core-auth";
export { OpenAIClient, OpenAIClientOptions } from "./OpenAIClient.js";
export { OpenAIKeyCredential } from "./OpenAIKeyCredential.js";
export * from "./models/audio.js";
export {
  AzureChatExtensionConfigurationUnion,
  AzureExtensionsOptions,
  Completions,
  ContentFilterResultsForPrompt,
  ContentFilterResultDetailsForPrompt,
  ContentFilterResult,
  ContentFilterSeverity,
  ContentFilterDetectionResult,
  ContentFilterBlocklistIdResult,
  Choice,
  ContentFilterResultsForChoice,
  ContentFilterSuccessResultDetailsForPrompt,
  ContentFilterErrorResults,
  ContentFilterSuccessResultsForChoice,
  ContentFilterCitedDetectionResult,
  CompletionsLogProbabilityModel,
  CompletionsFinishReason,
  CompletionsUsage,
  ChatRequestMessage,
  ChatRequestSystemMessage,
  ChatRequestUserMessage,
  ChatMessageContentItem,
  ChatMessageTextContentItem,
  ChatMessageImageContentItem,
  ChatMessageImageUrl,
  ChatMessageImageDetailLevel,
  ChatRequestAssistantMessage,
  ChatCompletionsToolCall,
  ChatCompletionsFunctionToolCall,
  FunctionCall,
  ChatRequestToolMessage,
  ChatRequestFunctionMessage,
  ChatRole,
  FunctionDefinition,
  FunctionCallPreset,
  FunctionName,
  AzureChatExtensionConfiguration,
  AzureSearchChatExtensionConfiguration,
  OnYourDataAuthenticationOptions,
  OnYourDataApiKeyAuthenticationOptions,
  OnYourDataConnectionStringAuthenticationOptions,
  OnYourDataKeyAndKeyIdAuthenticationOptions,
  OnYourDataEncodedApiKeyAuthenticationOptions,
  OnYourDataAccessTokenAuthenticationOptions,
  OnYourDataSystemAssignedManagedIdentityAuthenticationOptions,
  OnYourDataUserAssignedManagedIdentityAuthenticationOptions,
  OnYourDataAuthenticationType,
  AzureSearchIndexFieldMappingOptions,
  AzureSearchQueryType,
  OnYourDataVectorizationSource,
  OnYourDataEndpointVectorizationSource,
  OnYourDataDeploymentNameVectorizationSource,
  OnYourDataModelIdVectorizationSource,
  OnYourDataVectorizationSourceType,
  AzureMachineLearningIndexChatExtensionConfiguration,
  AzureCosmosDBChatExtensionConfiguration,
  AzureCosmosDBFieldMappingOptions,
  ElasticsearchChatExtensionConfiguration,
  ElasticsearchIndexFieldMappingOptions,
  ElasticsearchQueryType,
  PineconeChatExtensionConfiguration,
  PineconeFieldMappingOptions,
  AzureChatExtensionType,
  AzureChatEnhancementConfiguration,
  AzureChatGroundingEnhancementConfiguration,
  AzureChatOCREnhancementConfiguration,
  ChatCompletionsResponseFormat,
  ChatCompletionsTextResponseFormat,
  ChatCompletionsJsonResponseFormat,
  ChatCompletionsToolDefinition,
  ChatCompletionsFunctionToolDefinition,
  ChatCompletionsToolSelectionPreset,
  ChatCompletionsNamedToolSelection,
  ChatCompletionsNamedFunctionToolSelection,
  ChatCompletionsFunctionToolSelection,
  ChatCompletions,
  ChatChoice,
  ChatResponseMessage,
  AzureChatExtensionsMessageContext,
  AzureChatExtensionDataSourceResponseCitation,
  ChatChoiceLogProbabilityInfo,
  ChatTokenLogProbabilityResult,
  ChatTokenLogProbabilityInfo,
  ChatFinishDetails,
  StopFinishDetails,
  MaxTokensFinishDetails,
  AzureChatEnhancements,
  AzureGroundingEnhancement,
  AzureGroundingEnhancementLine,
  AzureGroundingEnhancementLineSpan,
  AzureGroundingEnhancementCoordinatePoint,
  ImageSize,
  ImageGenerationResponseFormat,
  ImageGenerationQuality,
  ImageGenerationStyle,
  ImageGenerations,
  ImageGenerationData,
  ImageGenerationContentFilterResults,
  ImageGenerationPromptFilterResults,
  Embeddings,
  EmbeddingItem,
  EmbeddingsUsage,
  ChatRequestMessageUnion,
  ChatMessageContentItemUnion,
  ChatCompletionsToolCallUnion,
  OnYourDataAuthenticationOptionsUnion,
  OnYourDataVectorizationSourceUnion,
  ChatCompletionsResponseFormatUnion,
  ChatCompletionsToolDefinitionUnion,
  ChatCompletionsNamedToolSelectionUnion,
  ChatFinishDetailsUnion,
  GetCompletionsOptions,
  GetChatCompletionsOptions,
  GetImagesOptions,
  GetEmbeddingsOptions,
  EventStream,
} from "./models/index.js";
